import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Info, ChevronDown, ChevronUp, BookOpen, Wrench, Check, Copy } from 'lucide-react';

const AdnexTagTroubleshooter = () => {
  const [activeTab, setActiveTab] = useState('troubleshooter');
  const [tagInput, setTagInput] = useState('');
  const [results, setResults] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Celtra Configurator state
  const [celtraInput, setCeltraInput] = useState('');
  const [celtraOutput, setCeltraOutput] = useState('');
  const [celtraFixes, setCeltraFixes] = useState([]);
  const [celtraCopied, setCeltraCopied] = useState(false);

  const colors = {
    bg: '#2e2829',
    surface: '#434343',
    electricBlue: '#6760ff',
    signalBlue: '#675fff',
    deepIndigo: '#3800aa',
    darkViolet: '#2d0384',
    white: '#ffffff',
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Celtra Tag Configurator Logic
  const fixCeltraTag = () => {
    if (!celtraInput.trim()) return;
    
    let tag = celtraInput;
    const fixes = [];
    
    const replacements = [
      { find: '[].slice.apply', replace: '\\[\\].slice.apply', name: '\\[\\].slice.apply', desc: 'Empty array literal' },
      { find: 'params[m[1]+(m[2]', replace: 'params\\[m\\[1\\]+(m\\[2\\]', name: 'params\\[m\\[1\\]+(m\\[2\\]', desc: 'First params access' },
      { find: '= decfs[m[3]]', replace: '= decfs\\[m\\[3\\]\\]', name: 'decfs\\[m\\[3\\]\\]', desc: 'decfs access' },
      { find: '(m[4].replace', replace: '(m\\[4\\].replace', name: 'm\\[4\\].replace', desc: 'm[4] access' },
      { find: 'params[k]', replace: 'params\\[k\\]', name: 'params\\[k\\]', desc: 'Loop params access' },
      { find: '([&#92;w%.]+)', replace: '(\\[&#92;w%.\\]+)', name: '(\\[&#92;w%.\\]+)', desc: 'Regex char class' },
      { find: '(&#92;[.+&#92;])?', replace: '(&#92;\\[.+&#92;\\])?', name: '(&#92;\\[.+&#92;\\])?', desc: 'Regex optional group' },
    ];
    
    replacements.forEach(r => {
      if (tag.includes(r.find)) {
        tag = tag.split(r.find).join(r.replace);
        fixes.push({ name: r.name, desc: r.desc, status: 'fixed' });
      } else if (tag.includes(r.replace)) {
        fixes.push({ name: r.name, desc: r.desc, status: 'already' });
      } else {
        fixes.push({ name: r.name, desc: r.desc, status: 'notfound' });
      }
    });
    
    setCeltraOutput(tag);
    setCeltraFixes(fixes);
  };

  const copyCeltraOutput = () => {
    navigator.clipboard.writeText(celtraOutput);
    setCeltraCopied(true);
    setTimeout(() => setCeltraCopied(false), 2000);
  };

  const handleCeltraPaste = async () => {
    try { 
      const text = await navigator.clipboard.readText(); 
      setCeltraInput(text);
      setCeltraOutput('');
      setCeltraFixes([]);
    } catch (err) {}
  };

  const analyzeTag = () => {
    if (!tagInput.trim()) return;

    const issues = [];
    const warnings = [];
    const passes = [];
    const info = [];
    const tag = tagInput;
    const lines = tag.split('\n');

    const findLineNumber = (searchStr) => {
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes(searchStr)) return i + 1;
      }
      return null;
    };

    const tagSize = new Blob([tag]).size;

    let tagType = 'unknown';
    let tagFormat = '';
    let vendor3PAS = 'Unknown';
    
    if (tag.includes('celtra') || tag.includes('ads.celtra.com')) {
      vendor3PAS = 'Celtra';
      tagType = 'celtra-display';
      tagFormat = 'Celtra Rich Media';
    } else if (tag.includes('gambid-video-loader') || tag.includes('gx-bid.com')) {
      vendor3PAS = 'Adnex';
      if (tag.includes('vastTagUrl')) { tagType = 'adnex-vast'; tagFormat = 'Adnex VAST-in-Banner'; }
      else if (tag.includes('directAdSrc')) { tagType = 'adnex-mp4'; tagFormat = 'Adnex Direct MP4'; }
      else { tagType = 'adnex-video'; tagFormat = 'Adnex Video'; }
    } else if (tag.includes('doubleclick.net') || tag.includes('googlesyndication') || tag.includes('dcm')) {
      vendor3PAS = 'Google DCM';
      tagType = 'google-display';
      tagFormat = 'Google Display';
    } else if (tag.includes('flashtalking')) {
      vendor3PAS = 'Flashtalking';
      tagType = 'flashtalking';
      tagFormat = 'Flashtalking';
    } else if (tag.includes('sizmek') || tag.includes('serving-sys.com')) {
      vendor3PAS = 'Sizmek';
      tagType = 'sizmek';
      tagFormat = 'Sizmek';
    } else if (tag.includes('adform')) {
      vendor3PAS = 'Adform';
      tagType = 'adform';
      tagFormat = 'Adform';
    } else if (tag.includes('<VAST')) {
      vendor3PAS = 'VAST XML';
      tagType = 'vast';
      tagFormat = 'VAST Video';
    } else if (tag.includes('<script') || tag.includes('<div')) {
      tagType = 'html-display';
      tagFormat = 'HTML Display';
    }

    info.push({ message: 'VENDOR: ' + vendor3PAS, detail: tagFormat });

    // DIMENSIONS
    const widthMatch = tag.match(/width['":\s]*(\d+)/i);
    const heightMatch = tag.match(/height['":\s]*(\d+)/i);
    
    if (tag.includes('[ADW]') && tag.includes('[ADH]')) {
      passes.push({ message: 'DIMENSIONS', detail: '[ADW] x [ADH] macros detected' });
    } else if (widthMatch && heightMatch) {
      info.push({ message: 'DIMENSIONS: ' + widthMatch[1] + 'x' + heightMatch[1], detail: 'Fixed' });
    } else {
      warnings.push({ message: 'DIMENSIONS', detail: 'Not detected', fix: 'Add width/height or use [ADW]/[ADH] macros' });
    }

    // QUOTES
    const quoteIssues = [];
    if (tag.includes('""')) quoteIssues.push('Empty ""');
    if (tag.includes("''")) quoteIssues.push("Empty ''");
    if ((tag.match(/"/g) || []).length % 2 !== 0) quoteIssues.push('Unbalanced "');
    if ((tag.match(/'/g) || []).length % 2 !== 0) quoteIssues.push("Unbalanced '");
    
    if (quoteIssues.length > 0) {
      issues.push({ message: 'QUOTE SYNTAX', detail: quoteIssues.join(', ') });
    } else {
      passes.push({ message: 'QUOTE SYNTAX', detail: 'Valid' });
    }

    // VIDEO
    const videoIndicators = ['video', 'vast', 'vpaid', 'mp4', 'webm', 'MediaFile'];
    const hasVideo = videoIndicators.some(v => tag.toLowerCase().includes(v.toLowerCase()));
    info.push({ message: 'VIDEO: ' + (hasVideo ? 'Yes' : 'No'), detail: hasVideo ? 'Video content detected' : 'Display only' });

    // CLICK URL
    if (tag.includes('[CLICK_URL]') || tag.includes('${CLICK_URL}') || tag.includes('%CLICK_URL%')) {
      passes.push({ message: 'CLICK TRACKING', detail: 'Click macro detected' });
    } else if (tag.includes('clickUrl') || tag.includes('click_url')) {
      warnings.push({ message: 'CLICK TRACKING', detail: 'Click parameter found but no DSP macro', fix: 'Add [CLICK_URL] macro' });
    } else {
      warnings.push({ message: 'CLICK TRACKING', detail: 'No click tracking detected' });
    }

    // CELTRA BRACKET CHECK
    if (vendor3PAS === 'Celtra') {
      const unescapedBrackets = [];
      if (tag.includes('[].slice.apply') && !tag.includes('\\[\\].slice.apply')) {
        unescapedBrackets.push('[].slice.apply');
      }
      if (tag.includes('params[m[1]') && !tag.includes('params\\[m\\[1\\]')) {
        unescapedBrackets.push('params[m[1]');
      }
      if (tag.includes('params[k]') && !tag.includes('params\\[k\\]')) {
        unescapedBrackets.push('params[k]');
      }
      
      if (unescapedBrackets.length > 0) {
        issues.push({
          message: 'CELTRA BRACKET ESCAPING',
          detail: 'Unescaped brackets will break DSP macro replacement: ' + unescapedBrackets.join(', '),
          fix: 'Use the Celtra Tag Configurator tab to fix these automatically',
          line: findLineNumber(unescapedBrackets[0])
        });
      } else if (tag.includes('\\[\\]') || tag.includes('\\[m\\[')) {
        passes.push({ message: 'CELTRA BRACKET ESCAPING', detail: 'Brackets properly escaped' });
      }

      // deviceId check
      if (!tag.includes('deviceId')) {
        warnings.push({ message: 'CELTRA DEVICE ID', detail: 'No deviceId parameter found', fix: 'Add deviceId=[IFA] to track survey responses' });
      } else {
        passes.push({ message: 'CELTRA DEVICE ID', detail: 'deviceId parameter present' });
      }
    }

    // HTTPS CHECK
    if (tag.includes('http://') && !tag.includes('https://')) {
      issues.push({ message: 'INSECURE PROTOCOL', detail: 'HTTP URLs will be blocked on HTTPS pages', fix: 'Replace http:// with https://' });
    } else if (tag.includes('https://')) {
      passes.push({ message: 'SECURE PROTOCOL', detail: 'HTTPS URLs detected' });
    }

    // SIZE CHECK
    if (tagSize > 150000) {
      warnings.push({ message: 'TAG SIZE', detail: Math.round(tagSize/1024) + 'KB - Large tags may timeout', fix: 'Consider hosting assets externally' });
    } else {
      passes.push({ message: 'TAG SIZE', detail: Math.round(tagSize/1024) + 'KB' });
    }

    // DSP MACROS
    const dspMacros = ['[CLICK_URL]', '[ADW]', '[ADH]', '[REQ_ID]', '[PIXEL_EVENT_VIMP]', '[IFA]', '[LAT]', '[LON]', '[APP_BUNDLE_ID]', '[SITEID]', '[PLACEMENT]', '[BID_ID]'];
    const foundMacros = dspMacros.filter(m => tag.includes(m));
    if (foundMacros.length > 0) {
      info.push({ message: 'DSP MACROS', detail: foundMacros.join(', ') });
    }

    setResults({
      issues,
      warnings,
      passes,
      info,
      tagSize,
      vendor3PAS,
      tagFormat,
      tagType
    });
  };

  const ResultSection = ({ title, items, icon: Icon, color, bgColor, sectionKey }) => {
    if (!items || items.length === 0) return null;
    const isExpanded = expandedSections[sectionKey] !== false;
    
    return (
      <div className={"border rounded-lg overflow-hidden mb-3 " + color}>
        <button 
          onClick={() => toggleSection(sectionKey)} 
          className={"w-full flex items-center justify-between p-3 " + bgColor}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            <span className="font-semibold">{title}</span>
            <span className="text-sm text-gray-600">({items.length})</span>
          </div>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {isExpanded && (
          <div className="bg-white border-t px-4 py-3 space-y-4">
            {items.map((item, idx) => (
              <div key={idx} className="border-l-2 border-gray-300 pl-4">
                <div className="flex items-start justify-between">
                  <p className="font-semibold text-gray-900">{item.message}</p>
                  {item.category && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">{item.category}</span>}
                </div>
                <p className="text-sm text-gray-700 mt-1">{item.detail}</p>
                {item.line && <p className="text-xs text-gray-500 mt-1">Line {item.line}</p>}
                {item.fix && (
                  <div className="mt-2 bg-green-50 border border-green-200 p-2 rounded">
                    <p className="text-xs font-semibold text-green-800 mb-1">FIX:</p>
                    <pre className="text-xs text-green-900 whitespace-pre-wrap font-mono">{item.fix}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const CeltraConfigurator = () => {
    const fixedCount = celtraFixes.filter(f => f.status === 'fixed').length;
    const alreadyCount = celtraFixes.filter(f => f.status === 'already').length;
    
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-purple-600" />
            Celtra Tag Configurator
          </h2>
          <p className="text-gray-600 mb-4">
            Paste your Celtra tag below. This tool automatically escapes JavaScript brackets that would otherwise be corrupted by DSP macro replacement.
          </p>
          
          <div className="flex items-center justify-between mb-2">
            <label className="font-semibold text-gray-700">Original Celtra tag:</label>
            <button 
              onClick={handleCeltraPaste} 
              className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              <Copy className="w-4 h-4" /> Paste from clipboard
            </button>
          </div>
          <textarea 
            value={celtraInput} 
            onChange={(e) => { setCeltraInput(e.target.value); setCeltraOutput(''); setCeltraFixes([]); }}
            placeholder="Paste your Celtra tag here..."
            className="w-full h-40 p-3 border rounded-lg font-mono text-xs"
          />
          
          <button 
            onClick={fixCeltraTag}
            disabled={!celtraInput.trim()}
            className={"w-full mt-4 py-3 rounded-lg font-bold text-white transition " + (celtraInput.trim() ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed')}
          >
            CONFIGURE CELTRA TAG
          </button>
        </div>

        {celtraFixes.length > 0 && (
          <div className="bg-white rounded-xl p-6">
            <h3 className="font-bold text-gray-900 mb-3">Configuration Results</h3>
            
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-green-50 p-3 rounded text-center">
                <p className="text-2xl font-bold text-green-600">{fixedCount}</p>
                <p className="text-xs text-green-700">Fixed</p>
              </div>
              <div className="bg-blue-50 p-3 rounded text-center">
                <p className="text-2xl font-bold text-blue-600">{alreadyCount}</p>
                <p className="text-xs text-blue-700">Already OK</p>
              </div>
              <div className="bg-gray-50 p-3 rounded text-center">
                <p className="text-2xl font-bold text-gray-600">{celtraFixes.filter(f => f.status === 'notfound').length}</p>
                <p className="text-xs text-gray-700">Not Found</p>
              </div>
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Element</th>
                    <th className="text-left py-2 px-2">Description</th>
                    <th className="text-left py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {celtraFixes.map((fix, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2 px-2 font-mono text-xs">{fix.name}</td>
                      <td className="py-2 px-2 text-gray-600">{fix.desc}</td>
                      <td className="py-2 px-2">
                        {fix.status === 'fixed' && <span className="text-green-600 font-medium">Fixed</span>}
                        {fix.status === 'already' && <span className="text-blue-600">Already OK</span>}
                        {fix.status === 'notfound' && <span className="text-gray-400">Not found</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {fixedCount > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
                  <CheckCircle className="w-5 h-5" />
                  Tag Configured Successfully
                </div>
                <p className="text-sm text-green-700">
                  {fixedCount} bracket escape(s) applied. The tag is now ready for trafficking.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-gray-700">Configured Celtra tag:</label>
              <button 
                onClick={copyCeltraOutput} 
                className={"flex items-center gap-1 text-sm font-medium " + (celtraCopied ? 'text-green-600' : 'text-purple-600 hover:text-purple-700')}
              >
                {celtraCopied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
            </div>
            <textarea 
              value={celtraOutput} 
              readOnly 
              className="w-full h-40 p-3 border rounded-lg font-mono text-xs bg-gray-50" 
            />
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-bold text-amber-900 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" /> Why is this needed?
          </h3>
          <p className="text-sm text-amber-800">
            Celtra tags use JavaScript bracket notation (e.g. [], params[k]) which the DSP's macro replacement system interprets as macro placeholders. Escaping brackets with backslashes (\[\]) prevents the DSP from matching them, keeping the JavaScript intact while still replacing legitimate macros like [CLICK_URL] and [IFA].
          </p>
        </div>
      </div>
    );
  };

  const KnowledgeCentre = () => (
    <div className="bg-white rounded-xl p-6 space-y-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        <BookOpen className="w-6 h-6 text-purple-600" />
        Adnex DSP Macros Reference
      </h2>
      
      <p className="text-gray-600 mb-4">
        These macros are placeholders replaced by the DSP at serve time. They must be included exactly as shown.
      </p>

      <div className="space-y-4">
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[CLICK_URL]</code>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Required</span>
          </div>
          <p className="text-sm text-gray-700">Click tracking redirect URL. The DSP replaces this with a URL that tracks the click before redirecting to the landing page.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[ADW]</code>
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[ADH]</code>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Recommended</span>
          </div>
          <p className="text-sm text-gray-700">Ad width and height in pixels. Replaced with the actual creative dimensions at serve time.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[REQ_ID]</code>
          </div>
          <p className="text-sm text-gray-700">Unique request ID for this ad impression. Used for deduplication and troubleshooting.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[PIXEL_EVENT_VIMP]</code>
          </div>
          <p className="text-sm text-gray-700">Viewable impression pixel URL. Fired when the ad meets viewability criteria (typically 50% in view for 1 second).</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[IFA]</code>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Mobile/CTV</span>
          </div>
          <p className="text-sm text-gray-700">Identifier for Advertising. The device's advertising ID (IDFA for iOS, GAID for Android). Used for frequency capping and attribution.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[LAT]</code>
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[LON]</code>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Location</span>
          </div>
          <p className="text-sm text-gray-700">GPS latitude and longitude coordinates. Available when the publisher passes location data.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[APP_BUNDLE_ID]</code>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Mobile</span>
          </div>
          <p className="text-sm text-gray-700">The app's bundle identifier (e.g. com.example.app). Used for app-level targeting and reporting.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[SITEID]</code>
          </div>
          <p className="text-sm text-gray-700">Publisher site identifier. Used for site-level reporting and blocklist/allowlist management.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[PLACEMENT]</code>
          </div>
          <p className="text-sm text-gray-700">Ad placement identifier within the publisher's inventory. Useful for placement-level optimisation.</p>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold">[BID_ID]</code>
          </div>
          <p className="text-sm text-gray-700">Unique bid identifier. Links the creative to the specific bid in the DSP's logs.</p>
        </div>

        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <code className="bg-gray-200 text-gray-800 px-2 py-1 rounded font-bold">${'${CLICK_URL$}'}</code>
            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">Alternate</span>
          </div>
          <p className="text-sm text-gray-700">Alternative click URL syntax used by some third-party ad servers. Functionally equivalent to [CLICK_URL].</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <h3 className="font-bold text-blue-900 mb-2">Best Practices</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Always include [CLICK_URL] for click tracking</li>
          <li>• Use [ADW] and [ADH] instead of hardcoded dimensions when possible</li>
          <li>• For Celtra tags, run through the Configurator tab first</li>
          <li>• Test tags in a preview environment before going live</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Adnex Tag Troubleshooter</h1>
          <p className="text-gray-400">Validate and configure ad tags for DSP compatibility</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('troubleshooter')}
            className={"flex-1 py-3 px-4 rounded-lg font-semibold transition " + 
              (activeTab === 'troubleshooter' 
                ? 'bg-white text-gray-900' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600')}
          >
            <Wrench className="w-4 h-4 inline mr-2" />
            Troubleshooter
          </button>
          <button
            onClick={() => setActiveTab('celtra')}
            className={"flex-1 py-3 px-4 rounded-lg font-semibold transition " + 
              (activeTab === 'celtra' 
                ? 'bg-white text-gray-900' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600')}
          >
            <Wrench className="w-4 h-4 inline mr-2" />
            Celtra Configurator
          </button>
          <button
            onClick={() => setActiveTab('knowledge')}
            className={"flex-1 py-3 px-4 rounded-lg font-semibold transition " + 
              (activeTab === 'knowledge' 
                ? 'bg-white text-gray-900' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600')}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Knowledge Centre
          </button>
        </div>

        {/* Content */}
        {activeTab === 'troubleshooter' && (
          <>
            <div className="bg-white rounded-xl p-6 mb-6">
              <label className="font-semibold text-gray-700 block mb-2">Paste your ad tag:</label>
              <textarea 
                value={tagInput} 
                onChange={(e) => { setTagInput(e.target.value); setResults(null); }}
                placeholder="Paste your HTML, JavaScript, or VAST tag here..."
                className="w-full h-48 p-3 border rounded-lg font-mono text-xs"
              />
              
              <button 
                onClick={analyzeTag}
                disabled={!tagInput.trim()}
                className={"w-full mt-4 py-3 rounded-lg font-bold text-white transition " + 
                  (tagInput.trim() ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed')}
              >
                ANALYZE TAG
              </button>
            </div>

            {results && (
              <div className="space-y-3">
                {/* Summary */}
                <div className="bg-white rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded font-bold">{results.vendor3PAS}</span>
                    <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded">{results.tagFormat}</span>
                    <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded">{Math.round(results.tagSize/1024)}KB</span>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-red-50 p-3 rounded text-center">
                      <p className="text-2xl font-bold text-red-600">{results.issues.length}</p>
                      <p className="text-xs text-red-700">Issues</p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded text-center">
                      <p className="text-2xl font-bold text-amber-600">{results.warnings.length}</p>
                      <p className="text-xs text-amber-700">Warnings</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded text-center">
                      <p className="text-2xl font-bold text-green-600">{results.passes.length}</p>
                      <p className="text-xs text-green-700">Passed</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded text-center">
                      <p className="text-2xl font-bold text-blue-600">{results.info.length}</p>
                      <p className="text-xs text-blue-700">Info</p>
                    </div>
                  </div>
                </div>

                <ResultSection title="Critical Issues" items={results.issues} icon={XCircle} color="border-red-300" bgColor="bg-red-50" sectionKey="issues" />
                <ResultSection title="Warnings" items={results.warnings} icon={AlertTriangle} color="border-amber-300" bgColor="bg-amber-50" sectionKey="warnings" />
                <ResultSection title="Passed" items={results.passes} icon={CheckCircle} color="border-green-300" bgColor="bg-green-50" sectionKey="passes" />
                <ResultSection title="Info" items={results.info} icon={Info} color="border-blue-300" bgColor="bg-blue-50" sectionKey="info" />
              </div>
            )}
          </>
        )}

        {activeTab === 'celtra' && <CeltraConfigurator />}
        {activeTab === 'knowledge' && <KnowledgeCentre />}
      </div>
    </div>
  );
};

export default AdnexTagTroubleshooter;
